import React from 'react';

/**
 * Check if a value has meaningful content
 * @param {*} value - Value to check (array or string)
 * @returns {boolean} - Whether the value has content
 */
export const hasContent = (value) => {
  return Array.isArray(value) ? value.length > 0 : (value && value.trim && value.trim() !== '');
};

/**
 * Render array values as chips/badges
 * @param {Array|string} value - Value to render
 * @returns {JSX.Element|null} - Rendered chips or null
 */
export const renderArray = (value) => {
  if (Array.isArray(value)) {
    return (
      <div className="flex flex-wrap gap-2">
        {value.map((item, index) => (
          <span key={index} className="inline-block bg-blue-100 text-blue-800 text-sm font-semibold px-3 py-1 rounded-3xl border border-blue-200">{item}</span>
        ))}
      </div>
    );
  } else if (value && value.trim && value.trim() !== '') {
    return (
      <span className="inline-block bg-blue-100 text-blue-800 text-sm font-semibold px-3 py-1 rounded-3xl border border-blue-200">{value.trim()}</span>
    );
  }
  return null;
};

/**
 * Render precautions/warnings with icons
 * @param {Array|string} value - Value to render
 * @param {boolean} isHazard - Whether this is a hazard (red) or precaution (yellow)
 * @returns {JSX.Element|null} - Rendered precautions or null
 */
export const renderPrecautions = (value, isHazard = false) => {
  if (Array.isArray(value)) {
    if (isHazard) {
      return (
        <div className="space-y-3">
          {value.map((item, index) => (
            <div key={`${item}_${index}`} className="flex items-start space-x-3">
              <i className="fas fa-exclamation-circle text-red-600 mt-1"></i>
              <span className="text-red-800 font-bold">{item}</span>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {value.map((item, index) => (
          <div key={`${item}_${index}`} className="flex items-start space-x-3">
            <i className="fas fa-exclamation-circle text-yellow-600 mt-1"></i>
            <span className="text-yellow-800 font-semibold" style={{ wordBreak: 'break-word' }}>{item}</span>
          </div>
        ))}
      </div>
    );
  } else if (value && value.trim && value.trim() !== '') {
    return (
      <div key={value} className="flex items-start space-x-3">
        <i className="fas fa-exclamation-circle text-yellow-600 mt-1"></i>
        <span className="text-yellow-800 font-semibold" style={{ wordBreak: 'break-word' }}>{value.trim()}</span>
      </div>
    );
  }
  return null;
};

/**
 * Get CSS class for toxicity card based on hazard level
 * @param {string} value - Toxicity level (Low, Moderate, High)
 * @returns {string} - Tailwind CSS classes
 */
export const toxicityCardClass = (value) => {
  if (value === 'Low') {
    return 'bg-green-100 text-green-800';
  } else if (value === 'Moderate') {
    return 'bg-yellow-100 text-yellow-800';
  } else if (value === 'High') {
    return 'bg-red-100 text-red-800';
  }
  return 'bg-gray-100 text-gray-800';
};

/**
 * Format LD50 Oral value and determine hazard level
 * @param {object} chemicalDetails - Chemical details object
 * @returns {[string, string]} - [formatted value, hazard level]
 */
export const formatLD50Oral = (chemicalDetails) => {
  const val = chemicalDetails?.toxic_release_measures?.acute_toxicity?.ld50?.oral?.value;
  if (!val) return ['', ''];

  const value = parseFloat(val.split(' ')[0]);
  const unit = val.split(' ')[1];

  let hazard = '';
  if (value > 2000) {
    hazard = 'Low';
  } else if (value >= 300 && value <= 2000) {
    hazard = 'Moderate';
  } else if (value < 300) {
    hazard = 'High';
  }

  return [`${value} ${unit}`, hazard];
};

/**
 * Format LD50 Dermal value and determine hazard level
 * @param {object} chemicalDetails - Chemical details object
 * @returns {[string, string]} - [formatted value, hazard level]
 */
export const formatLD50Dermal = (chemicalDetails) => {
  const val = chemicalDetails?.toxic_release_measures?.acute_toxicity?.ld50?.dermal?.value;
  if (!val) return ['', ''];

  const value = parseFloat(val.split(' ')[0]);
  const unit = val.split(' ')[1];

  let hazard = '';
  if (value > 2000) {
    hazard = 'Low';
  } else if (value >= 200 && value <= 2000) {
    hazard = 'Moderate';
  } else if (value < 200) {
    hazard = 'High';
  }

  return [`${value} ${unit}`, hazard];
};

/**
 * Format LC50 Inhalation value and determine hazard level
 * @param {object} chemicalDetails - Chemical details object
 * @returns {[string, string]} - [formatted value, hazard level]
 */
export const formatLC50Inhalation = (chemicalDetails) => {
  const val = chemicalDetails?.toxic_release_measures?.acute_toxicity?.lc50?.inhalation?.value;
  if (!val) return ['', ''];

  const value = parseFloat(val.split(' ')[0]);
  const unit = val.split(' ')[1];

  let hazard = '';
  if (unit === 'ppm') {
    if (value > 5000) {
      hazard = 'Low';
    } else if (value >= 100 && value <= 5000) {
      hazard = 'Moderate';
    } else if (value < 100) {
      hazard = 'High';
    }
  } else if (unit === 'mg/l') {
    if (value > 2) {
      hazard = 'Low';
    } else if (value >= 0.2 && value <= 2) {
      hazard = 'Moderate';
    } else if (value < 0.2) {
      hazard = 'High';
    }
  }

  return [`${value} ${unit}`, hazard];
};

/**
 * Check if LD50 data is available
 * @param {object} chemicalDetails - Chemical details object
 * @returns {boolean} - Whether LD50 data exists
 */
export const checkLD50 = (chemicalDetails) => {
  const val1 = chemicalDetails?.toxic_release_measures?.acute_toxicity?.ld50?.oral?.value;
  const val2 = chemicalDetails?.toxic_release_measures?.acute_toxicity?.ld50?.dermal?.value;
  return Boolean(val1 || val2);
};

/**
 * Check if LC50 data is available
 * @param {object} chemicalDetails - Chemical details object
 * @returns {boolean} - Whether LC50 data exists
 */
export const checkLC50 = (chemicalDetails) => {
  const val = chemicalDetails?.toxic_release_measures?.acute_toxicity?.lc50?.inhalation?.value;
  return Boolean(val);
};
